import React, { memo, useState, useEffect } from "react";
import { getThumbnailUrl } from '@msaliceb/shared/thumbnail';
import { fetchVideos } from '@/utils/video-queries';
import SectionSlider from "../slider/SectionSlider";
import TopTenCard from "../cards/TopTenCard";

interface VideoItem {
  _id: string;
  _type: 'video';
  title?: string;
  thumbnailPortrait?: any;
  thumbnailLandscape?: any;
  slug: { current: string };
  index?: number;
  data?: VideoItem;
}

interface HomeFeedSectionProps {
  title: string;
  filter: any; // GROQ filter string or object
  limit?: number;
  link?: string;
}

const HomeFeedSection = memo(({ title, filter, limit = 10, link }: HomeFeedSectionProps) => {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchVideos(limit, filter);
        if (!data || data.length === 0) {
          setError("No videos found. Please check your Sanity content.");
          setLoading(false);
          setVideos([]);
          return;
        }
        const videosWithIndex = data.map((video: VideoItem, index: number) => ({
          ...video,
          index: index
        }));
        setVideos(videosWithIndex);
        setLoading(false);
      } catch (error: any) {
        setError("Failed to load videos. Please try again later.");
        setLoading(false);
        setVideos([]);
      }
    };
    fetchData();
  }, [filter, limit]);

  if (loading) {
    return (
      <div className="section-padding">
        <h4 className="main-title mb-3">{title}</h4>
        <div className="row">
          {Array(5).fill(0).map((_, index) => (
            <div key={index} className="col-6 col-sm-4 col-md-3 col-lg-2 mb-4">
              <div className="card skeleton-card">
                <div className="skeleton-image"></div>
                <div className="skeleton-title"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return <div className="error text-center p-4">{error}</div>;
  }
  if (videos.length === 0) {
    return <div className="text-center p-4">No videos found</div>;
  }

  return (
    <SectionSlider title={title} list={videos} className="home-feed-slider" link={link}>
      {(data: VideoItem) => {
        const fallbackImageUrl = "/assets/images/bellyfit-cfp-banner-July-24-overview.jpg";
        const videoData = data.data || data;
        const resolvedThumbnailUrl = getThumbnailUrl({
          video: videoData as any,
          orientation: 'portrait',
          fallbackUrl: fallbackImageUrl,
          width: 522,
          height: 760
        });
        const thumbnailUrl = resolvedThumbnailUrl || fallbackImageUrl;
        const altText = videoData.thumbnailPortrait?.alt || videoData.thumbnailLandscape?.alt || videoData.title || "Movie thumbnail";
        return (
          <TopTenCard
            imagePath={thumbnailUrl}
            countValue={(data.index || 0) + 1}
            link={`/videos/${videoData.slug?.current || 'detail'}`}
            title={videoData.title}
            alt={altText}
          />
        );
      }}
    </SectionSlider>
  );
});

HomeFeedSection.displayName = "HomeFeedSection";
export default HomeFeedSection;
